import React, { FC, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Radio, Select, Typography, Button, Space, Divider, Spin } from "antd";
import type { RadioChangeEvent } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { Props, defaultData, VerseMode } from "./types";
import { getAvailableVersions, getDefaultVersionId, loadBibleData } from "../../../services/bible";
import { useValue } from "../../../lib/db/react";
import { db } from "../../../db/state";
import { messages } from "./messages";

const { Text, Title } = Typography;

interface BookOption {
  bookId: string;
  name: string;
}

interface VerseMeta {
  Book: string;
  Chapter: string;
  Verse: string;
  Text?: string;
}

const getLocaleOverride = (): string | null => {
  try { return new URLSearchParams(window.location.search).get("locale"); } catch { return null; }
};

const VerseSettings: FC<Props> = ({ data = defaultData, setData }) => {
  const dbLocale = useValue(db, "locale");
  const locale = getLocaleOverride() || dbLocale;
  const intl = useIntl();
  const versions = useMemo(() => {
    const all = getAvailableVersions();
    const defaultId = getDefaultVersionId(locale);
    const defaultVersion = all.find((v) => v.id === defaultId);
    if (!defaultVersion) return all;
    const sameLanguage = all.filter((v) => v.language === defaultVersion.language);
    const others = all.filter((v) => v.language !== defaultVersion.language);
    return [...sameLanguage, ...others];
  }, [locale]);
  const [books, setBooks] = useState<BookOption[]>([]);
  const [versesMeta, setVersesMeta] = useState<VerseMeta[]>([]);
  const [verseTexts, setVerseTexts] = useState<Map<string, string>>(new Map());
  const [metaLoading, setMetaLoading] = useState(false);
  const mode = data.mode || defaultData.mode;
  const rawSelectedVersionIds = data.manualVersionIds || [];
  const selectedVersionIds = useMemo(
    () => versions.filter((v) => rawSelectedVersionIds.includes(v.id)).map((v) => v.id),
    [versions, rawSelectedVersionIds]
  );
  const primaryManualVersionId = selectedVersionIds[0] || getDefaultVersionId(locale);
  const defaultManualVersionId = getDefaultVersionId(locale);

  useEffect(() => {
    if (mode !== "manual") return;
    const current = data.manualVersionIds || [];
    if (current.length === 0) {
      setData({ ...data, manualVersionIds: [defaultManualVersionId] });
    }
  }, [mode, locale]);

  useEffect(() => {
    if (mode !== "manual") return;

    const versionId = primaryManualVersionId;
    setMetaLoading(true);
    loadBibleData(versionId)
      .then((bible: any) => {
        const bookIndexByAlias = new Map<string, string>();
        const nextBooks = (bible.books || []).map((b: any, index: number) => {
          const bookId = String(index + 1);
          [bookId, b._id, b.book_id, b.name, b.acronym_name].forEach((alias) => {
            if (alias != null && String(alias).trim()) {
              bookIndexByAlias.set(String(alias), bookId);
            }
          });
          return {
            bookId,
            name: b.name || b.book_id || bookId,
          };
        });

        const nextVerses: VerseMeta[] = [];
        const textMap = new Map<string, string>();
        (bible.verses || []).forEach((v: any) => {
          const mappedBook =
            bookIndexByAlias.get(String(v.Book)) ||
            (Number.isFinite(Number(v.Book)) ? String(Number(v.Book)) : "");
          if (!mappedBook) return;
          const key = `${mappedBook}|${String(v.Chapter)}|${String(v.Verse)}`;
          if (!textMap.has(key) && v.Text) {
            textMap.set(key, String(v.Text));
          }
          nextVerses.push({
            Book: mappedBook,
            Chapter: String(v.Chapter),
            Verse: String(v.Verse),
            Text: String(v.Text || ""),
          });
        });

        setBooks(nextBooks);
        setVersesMeta(nextVerses);
        setVerseTexts(textMap);
      })
      .catch(() => {
        setBooks([]);
        setVersesMeta([]);
      })
      .finally(() => {
        setMetaLoading(false);
      });
  }, [mode, locale, primaryManualVersionId]);

  const handleModeChange = (e: RadioChangeEvent) => {
    const nextMode = e.target.value;
    const next: any = { ...data, mode: nextMode };
    if (nextMode === 'manual' && (!data.manualVersionIds || data.manualVersionIds.length === 0)) {
      next.manualVersionIds = [getDefaultVersionId(locale)];
    }
    setData(next);
  };

  const handleRefresh = () => {
    setData({ ...data, seedOffset: (data.seedOffset || 0) + 1 });
  };

  const selectedBook = data.manualBook || "";
  const selectedChapter = data.manualChapter || "";
  const selectedVerse = data.manualVerse || "";

  const availableChapters = useMemo(() => {
    if (!selectedBook) return [] as string[];
    return Array.from(
      new Set(
        versesMeta
          .filter((item) => String(item.Book) === String(selectedBook))
          .map((item) => String(item.Chapter))
      )
    ).sort((a, b) => Number(a) - Number(b));
  }, [versesMeta, selectedBook]);

  const availableVerses = useMemo(() => {
    if (!selectedBook || !selectedChapter) return [] as string[];
    return Array.from(
      new Set(
        versesMeta
          .filter(
            (item) =>
              String(item.Book) === String(selectedBook) &&
              String(item.Chapter) === String(selectedChapter)
          )
          .map((item) => String(item.Verse))
      )
    ).sort((a, b) => Number(a) - Number(b));
  }, [versesMeta, selectedBook, selectedChapter]);

  const chapterCount = availableChapters.length;
  const verseCount = availableVerses.length;

  const bookOptions = useMemo(
    () =>
      books.map((b) => {
        const count = Array.from(
          new Set(
            versesMeta
              .filter((item) => String(item.Book) === String(b.bookId))
              .map((item) => String(item.Chapter))
          )
        ).length;
        return {
          value: b.bookId,
          label: `${b.name} · ${intl.formatMessage(messages.chaptersCount, { count })}`,
        };
      }),
    [books, versesMeta, intl]
  );

  const chapterOptions = useMemo(
    () =>
      availableChapters.map((chapter) => {
        const count = Array.from(
          new Set(
            versesMeta
              .filter(
                (item) =>
                  String(item.Book) === String(selectedBook) &&
                  String(item.Chapter) === String(chapter)
              )
              .map((item) => String(item.Verse))
          )
        ).length;
        return {
          value: chapter,
          label: `${chapter} · ${intl.formatMessage(messages.versesCount, { count })}`,
        };
      }),
    [availableChapters, versesMeta, selectedBook, intl]
  );

  const verseOptions = useMemo(
    () =>
      availableVerses.map((verse) => {
        const key = `${selectedBook}|${selectedChapter}|${verse}`;
        const snippet = verseTexts.get(key) || "";
        const preview = snippet
          ? snippet.length > 28
            ? snippet.slice(0, 28) + "…"
            : snippet
          : "";
        return {
          value: verse,
          label: preview ? `${verse}  ${preview}` : verse,
        };
      }),
    [availableVerses, verseTexts, selectedBook, selectedChapter]
  );

  const handleBookChange = (bookId: string) => {
    const nextChapters = Array.from(
      new Set(
        versesMeta
          .filter((item) => String(item.Book) === String(bookId))
          .map((item) => String(item.Chapter))
      )
    ).sort((a, b) => Number(a) - Number(b));

    const nextChapter = nextChapters[0];
    const nextVerses = nextChapter
      ? Array.from(
          new Set(
            versesMeta
              .filter(
                (item) =>
                  String(item.Book) === String(bookId) &&
                  String(item.Chapter) === String(nextChapter)
              )
              .map((item) => String(item.Verse))
          )
        ).sort((a, b) => Number(a) - Number(b))
      : [];

    setData({
      ...data,
      manualBook: bookId,
      manualChapter: nextChapter,
      manualVerse: nextVerses[0],
    });
  };

  const handleChapterChange = (chapter: string) => {
    const nextVerses = Array.from(
      new Set(
        versesMeta
          .filter(
            (item) =>
              String(item.Book) === String(selectedBook) &&
              String(item.Chapter) === String(chapter)
          )
          .map((item) => String(item.Verse))
      )
    ).sort((a, b) => Number(a) - Number(b));

    setData({
      ...data,
      manualChapter: chapter,
      manualVerse: nextVerses[0],
    });
  };

  const handleVersionToggle = (versionId: string, checked: boolean) => {
    const currentSet = new Set(selectedVersionIds);
    if (checked) {
      currentSet.add(versionId);
    } else {
      currentSet.delete(versionId);
    }
    const next = versions.map((v) => v.id).filter((id) => currentSet.has(id)).slice(0, 5);
    setData({ ...data, manualVersionIds: next });
  };

  useEffect(() => {
    if (mode !== "manual" || books.length === 0 || versesMeta.length === 0) return;

    if (!selectedBook || !books.some((book) => book.bookId === selectedBook)) {
      handleBookChange(books[0].bookId);
      return;
    }

    if (!selectedChapter || !availableChapters.includes(selectedChapter)) {
      if (availableChapters[0]) handleChapterChange(availableChapters[0]);
      return;
    }

    if (!selectedVerse || !availableVerses.includes(selectedVerse)) {
      if (availableVerses[0]) {
        setData({ ...data, manualVerse: availableVerses[0] });
      }
    }
  }, [
    mode,
    books,
    versesMeta,
    selectedBook,
    selectedChapter,
    selectedVerse,
    availableChapters,
    availableVerses,
  ]);

  const modeOptions: { value: VerseMode; label: string; desc: string }[] = useMemo(
    () => [
      { value: "full", label: intl.formatMessage(messages.modeFull), desc: intl.formatMessage(messages.modeFullDesc) },
      { value: "classic", label: intl.formatMessage(messages.modeClassic), desc: intl.formatMessage(messages.modeClassicDesc) },
      { value: "manual", label: intl.formatMessage(messages.modeManual), desc: intl.formatMessage(messages.modeManualDesc) },
    ],
    [intl]
  );

  return (
    <div className="VerseSettings" style={{ padding: "2px 0 0" }}>
      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        <div>
          <Title level={5} style={{ margin: 0, lineHeight: 1.2 }}>
            {intl.formatMessage(messages.title)}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {intl.formatMessage(messages.subtitle)}
          </Text>
          {mode !== "manual" && (
            <Button
              block
              size="middle"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              style={{ marginTop: 10, borderRadius: 10 }}
            >
              {intl.formatMessage(messages.refresh)}
            </Button>
          )}
        </div>

        <Divider style={{ margin: "4px 0" }} />

        <div>
          <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
            <Text strong style={{ display: "block", fontSize: 13 }}>
              {intl.formatMessage(messages.source)}
            </Text>
            <a
              href="https://bible.aliving.stream"
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 12 }}
            >
              {intl.formatMessage(messages.readBible)}
            </a>
          </Space>
          <Radio.Group value={mode} onChange={handleModeChange} style={{ display: "grid", gap: 8 }}>
            {modeOptions.map((opt) => {
              const selected = mode === opt.value;
              return (
                <label
                  key={opt.value}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    cursor: "pointer",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: `1px solid ${selected ? "#1677ff" : "#e5e7eb"}`,
                    background: selected ? "rgba(22,119,255,0.04)" : "#fff",
                  }}
                >
                  <Radio value={opt.value} style={{ marginTop: 1 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>{opt.label}</div>
                    <div style={{ color: "#8c8c8c", fontSize: 12, lineHeight: 1.35, marginTop: 2 }}>
                      {opt.desc}
                    </div>
                  </div>
                </label>
              );
            })}
          </Radio.Group>
        </div>

        {mode === "manual" && (
          <div>
            <Divider style={{ margin: "8px 0 10px" }} />
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <div>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text strong style={{ display: "block", fontSize: 13 }}>
                    {intl.formatMessage(messages.reference)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {chapterCount} ch · {verseCount} v
                  </Text>
                </Space>
                <div style={{ marginTop: 6 }}>
                  {metaLoading && (
                    <div style={{ marginBottom: 6, fontSize: 12, color: "#8c8c8c", fontStyle: "italic" }}>
                      <Spin size="small" style={{ marginRight: 6 }} />
                      {intl.formatMessage(messages.loadingBooks)}
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
                    <Select
                      size="small"
                      showSearch
                      placeholder={intl.formatMessage(messages.bookPlaceholder)}
                      value={selectedBook || undefined}
                      onChange={handleBookChange}
                      filterOption={(input, option) =>
                        (option?.label as string || "").toLowerCase().includes(input.toLowerCase())
                      }
                      options={bookOptions}
                      disabled={metaLoading}
                    />
                    <Select
                      size="small"
                      placeholder={intl.formatMessage(messages.chapterPlaceholder)}
                      value={selectedChapter || undefined}
                      onChange={handleChapterChange}
                      disabled={metaLoading || !selectedBook}
                      options={chapterOptions}
                    />
                  </div>
                  <Select
                    size="small"
                    placeholder={intl.formatMessage(messages.versePlaceholder)}
                    value={selectedVerse || undefined}
                    onChange={(verse) => setData({ ...data, manualVerse: verse })}
                    disabled={metaLoading || !selectedBook || !selectedChapter}
                    options={verseOptions}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>

              <div>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text strong style={{ fontSize: 13 }}>{intl.formatMessage(messages.versions)}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {selectedVersionIds.length}/5
                  </Text>
                </Space>
                <div style={{ marginTop: 4 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
                    {versions.map((v) => {
                      const checked = selectedVersionIds.includes(v.id);
                      const disabled = !checked && selectedVersionIds.length >= 5;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          title={v.name}
                          onClick={() => {
                            if (!disabled) handleVersionToggle(v.id, !checked);
                          }}
                          disabled={disabled}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "16px minmax(0, 1fr)",
                            alignItems: "center",
                            columnGap: 8,
                            minWidth: 0,
                            padding: "3px 0",
                            border: "none",
                            background: "transparent",
                            textAlign: "left",
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? "not-allowed" : "pointer",
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              border: checked ? "1px solid #1677ff" : "1px solid #cfd4dc",
                              background: checked ? "#1677ff" : "#fff",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: 11,
                              lineHeight: 1,
                              boxSizing: "border-box",
                            }}
                          >
                            {checked ? "✓" : ""}
                          </span>
                          <span
                            style={{
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: 13,
                              lineHeight: "16px",
                              color: "inherit",
                            }}
                          >
                            {v.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Space>
          </div>
        )}
      </Space>
    </div>
  );
};

export default VerseSettings;
